import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ShortcutAction } from '../lib/shortcuts'

/**
 * Which keyboard bindings the screen on view can actually honor.
 *
 * `lib/shortcuts.ts` has mapped `I` to the inspector and Escape to a dismissal since the shell
 * landed, and `Shell.tsx` handled `command-bar` and dropped every other action on the floor - while
 * the views pane printed "Inspector · I" on every route. That file's own comment says why this had to
 * be fixed rather than tidied around: "a panel documenting shortcuts that do nothing is worse than no
 * panel; it teaches an operator to reach for something that will not answer, and the second time they
 * try it they stop trusting the rest of the screen too" (D-447).
 *
 * A registry rather than a prop threaded down from the shell, because the shell must not know which
 * screens have an inspector. A screen registers what it can do on mount and gives it back on unmount;
 * the pane advertises exactly what is registered, so the hint list is true per route rather than true
 * on average.
 */
export type ShortcutHandlers = Partial<Record<ShortcutAction, () => void>>

interface Registry {
  handlers: ShortcutHandlers
  register: (handlers: ShortcutHandlers) => () => void
}

const ShortcutContext = createContext<Registry>({ handlers: {}, register: () => () => {} })

export function ShortcutProvider({ children }: { children: React.ReactNode }) {
  const [handlers, setHandlers] = useState<ShortcutHandlers>({})

  const value = useMemo<Registry>(
    () => ({
      handlers,
      register: (next: ShortcutHandlers) => {
        setHandlers(next)
        // Cleared on unmount rather than merged away, because one screen owns the set at a time and a
        // stale handler from the screen somebody just left is a keystroke that acts on nothing.
        return () => setHandlers({})
      }
    }),
    [handlers]
  )

  return <ShortcutContext.Provider value={value}>{children}</ShortcutContext.Provider>
}

/** What the shell dispatches into, and what the views pane filters its hint list by. */
export function useShortcutRegistry(): Registry {
  return useContext(ShortcutContext)
}

/**
 * Registers this screen's bindings for as long as it is mounted.
 *
 * The handlers are re-registered whenever the caller's object identity changes, which for a component
 * holding state is every render - so callers pass a memoized object. Registering fresh closures each
 * render would otherwise be the way an action acts on the state it had three renders ago.
 */
export function useShortcuts(handlers: ShortcutHandlers): void {
  const { register } = useShortcutRegistry()
  useEffect(() => register(handlers), [register, handlers])
}
